import type { Product } from '@recipes/contracts';
import { Alert, Button, Card, CardContent } from '@recipes/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, MessageSquarePlus, Pencil, Plus, ScanBarcode, WifiOff } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { toast } from '@/components/feedback/toaster';
import { HintDialog } from '@/features/catalog/hint-dialog';
import {
  useCatalogBoot,
  useCatalogHints,
  useCatalogProduct,
  useCatalogSyncState,
  useProductPending,
  useShoppingAt,
} from '@/features/catalog/hooks';
import { lookupBarcode, type ScanOutcome } from '@/features/catalog/lookup';
import { ProductCard } from '@/features/catalog/product-card';
import { ProductForm } from '@/features/catalog/product-form';
import { ScannerView } from '@/features/catalog/scanner-view';
import { ShoppingAtControl } from '@/features/catalog/shopping-at';
import { useLocalList } from '@/features/shopping/hooks';
import { addItemLocal, setCheckedLocal } from '@/features/shopping/shopping-queue';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { queryKeys } from '@/lib/query-keys';
import { currentFormatLocale } from '@/lib/i18n';

type Found = Extract<ScanOutcome, { status: 'found' }>;

/** Live view of a scanned product: the device copy may change while the card is open. */
function FoundProduct({
  found,
  listId,
  toPantry,
  onNext,
}: {
  found: Found;
  listId: string | null;
  toPantry: boolean;
  onNext: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { store } = useShoppingAt();
  const [current, setCurrent] = useState<Product>(found.product);
  const stored = useCatalogProduct(current.id);
  const product = stored ?? current;
  const liveHints = useCatalogHints({
    productId: product.id,
    ingredientId: product.ingredient?.id ?? null,
  });
  const hints = liveHints.length ? liveHints : found.hints;
  const [hintOpen, setHintOpen] = useState(false);
  // Captured on this device and not confirmed by the server yet: its id may still change.
  // Until the queue was read, what the lookup said counts.
  const pending = useProductPending(product.id) ?? found.pending;

  // The list comes from the device copy and changes go through its queue, so scanning,
  // adding and checking off work at the shelf without a connection.
  const list = useLocalList(listId ?? '');
  const openItem = list.items.find(
    (item) =>
      !item.checked &&
      (item.product?.id === product.id ||
        (product.ingredient && item.ingredientId === product.ingredient.id)),
  );
  const refreshList = () =>
    void queryClient.invalidateQueries({ queryKey: queryKeys.shopping.all });

  const addToList = useMutation({
    mutationFn: () =>
      addItemLocal(listId ?? '', {
        name: product.ingredient?.name ?? product.name,
        productId: pending ? undefined : product.id,
      }),
    onSuccess: () => {
      toast.success(t('catalog.scan.addedToList'));
      refreshList();
    },
    onError: (e) => toastError(e),
  });
  const checkOff = useMutation({
    mutationFn: async () => {
      if (openItem) await setCheckedLocal(openItem, true, pending ? undefined : product.id);
    },
    onSuccess: () => {
      toast.success(t('catalog.scan.checkedOff', { name: openItem?.name ?? product.name }));
      refreshList();
    },
    onError: (e) => toastError(e),
  });
  const addToPantry = useMutation({
    mutationFn: () =>
      api.pantry.create({
        name: product.ingredient?.name ?? product.name,
        quantity: product.packageQuantity,
        unitCode: product.packageUnitCode,
        unitText: product.packageUnitText,
        productId: pending ? undefined : product.id,
      }),
    onSuccess: () => {
      toast.success(t('catalog.scan.addedToPantry'));
      void queryClient.invalidateQueries({ queryKey: queryKeys.pantry.all });
    },
    onError: (e) => toastError(e),
  });

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <ProductCard
          product={product}
          hints={hints}
          conflicts={found.conflicts}
          store={store}
          pending={pending}
          onProductChanged={setCurrent}
          actions={
            <>
              {listId &&
                (openItem ? (
                  <Button
                    onClick={() => checkOff.mutate()}
                    loading={checkOff.isPending}
                    data-testid="scan-check-off"
                  >
                    <Check aria-hidden /> {t('catalog.scan.checkOff', { name: openItem.name })}
                  </Button>
                ) : (
                  <Button
                    onClick={() => addToList.mutate()}
                    loading={addToList.isPending}
                    data-testid="scan-add-to-list"
                  >
                    <Plus aria-hidden /> {t('catalog.scan.addToList')}
                  </Button>
                ))}
              {toPantry && (
                <Button
                  onClick={() => addToPantry.mutate()}
                  loading={addToPantry.isPending}
                  data-testid="scan-add-to-pantry"
                >
                  <Plus aria-hidden /> {t('catalog.scan.addToPantry')}
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => setHintOpen(true)}
                data-testid="scan-add-hint"
              >
                <MessageSquarePlus aria-hidden /> {t('catalog.hint.add')}
              </Button>
              <Button asChild variant="outline">
                <Link to={`/catalog/products/${product.id}`}>
                  <Pencil aria-hidden /> {t('catalog.product.correct')}
                </Link>
              </Button>
              <Button variant="ghost" onClick={onNext} data-testid="scan-next">
                <ScanBarcode aria-hidden /> {t('catalog.scan.next')}
              </Button>
            </>
          }
        />
        <HintDialog
          open={hintOpen}
          onOpenChange={setHintOpen}
          target={{ productId: product.id, name: product.name }}
          defaultStoreId={store?.id ?? null}
        />
      </CardContent>
    </Card>
  );
}

/**
 * Scan in the supermarket (docs/finished/ingredient-catalog-spec.md 5.1): known products answer from
 * the device copy without any request; unknown ones are looked up online or captured on the
 * spot. `?listId=` adds "add to list / check off", `?target=pantry` adds "add to pantry".
 */
export function CatalogScanPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const listId = params.get('listId');
  const toPantry = params.get('target') === 'pantry';
  const online = useOnlineStatus();
  const syncState = useCatalogSyncState();
  const [outcome, setOutcome] = useState<ScanOutcome | null>(null);
  const [busy, setBusy] = useState(false);
  useCatalogBoot();

  const onCode = useCallback(async (barcode: string) => {
    setBusy(true);
    try {
      setOutcome(await lookupBarcode(barcode));
    } catch (error) {
      toastError(error);
    } finally {
      setBusy(false);
    }
  }, []);

  // A deep link with ?barcode= (tests, links from other screens) starts with that product.
  const initial = params.get('barcode');
  useEffect(() => {
    if (initial) void onCode(initial);
  }, [initial, onCode]);

  const reset = () => setOutcome(null);
  const syncedAt = syncState?.syncedAt
    ? new Intl.DateTimeFormat(currentFormatLocale(), {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(syncState.syncedAt))
    : null;

  return (
    <PageContainer className="max-w-xl">
      <FeaturePageHeader
        title={t('catalog.scan.title')}
        description={t('catalog.scan.description')}
        backTo={listId ? `/shopping/${listId}` : toPantry ? '/pantry' : '/catalog/products'}
      />
      <div className="mb-3">
        <ShoppingAtControl />
      </div>
      {!online && (
        <p
          role="status"
          className="mb-3 flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-xs"
          data-testid="scan-offline"
        >
          <WifiOff className="size-4" aria-hidden />
          {syncedAt ? t('catalog.offlineAsOf', { time: syncedAt }) : t('catalog.offlineNoCopy')}
        </p>
      )}

      <ScannerView onCode={(code) => void onCode(code)} paused={busy || outcome !== null} />

      <div className="mt-4" aria-live="polite">
        {busy && <p className="text-sm text-muted-foreground">{t('catalog.scan.lookingUp')}</p>}
        {outcome?.status === 'invalid' && (
          <Alert variant="destructive">{t('catalog.scanner.invalid')}</Alert>
        )}
        {outcome?.status === 'found' && (
          <FoundProduct
            key={outcome.product.id}
            found={outcome}
            listId={listId}
            toPantry={toPantry}
            onNext={reset}
          />
        )}
        {outcome?.status === 'unknown' && (
          <Card data-testid="scan-unknown">
            <CardContent className="space-y-3 p-4">
              <h2 className="font-semibold">{t('catalog.scan.unknownTitle')}</h2>
              <p className="text-sm text-muted-foreground">
                {outcome.offline ? t('catalog.scan.unknownOffline') : t('catalog.scan.unknownBody')}
              </p>
              <ProductForm
                barcode={outcome.barcode}
                onCancel={reset}
                onSaved={(product) =>
                  setOutcome({
                    status: 'found',
                    product,
                    hints: [],
                    conflicts: [],
                    origin: 'DEVICE',
                    pending: true,
                  })
                }
              />
            </CardContent>
          </Card>
        )}
      </div>
    </PageContainer>
  );
}
